import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Play, Square, Clock } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { TimeEntry } from "@/types/time-entry";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { calculateDurationSeconds, formatElapsed } from "@/utils/timeCalculations";

export function TimeTracker() {
  const [currentEntry, setCurrentEntry] = useState<TimeEntry | null>(null);
  const [description, setDescription] = useState("");
  const [elapsedDisplay, setElapsedDisplay] = useState("0:00");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Derived — no separate isTracking state
  const isTracking = currentEntry !== null;

  // Resume any ongoing entry on mount
  useEffect(() => {
    if (!user) return;
    supabase
      .from("time_entries")
      .select("*")
      .eq("user_id", user.id)
      .is("end_time", null)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setCurrentEntry(data);
      });
  }, [user]);

  // Tick elapsed display — guaranteed cleanup, no memory leak
  useEffect(() => {
    if (!currentEntry) {
      setElapsedDisplay("0:00");
      return;
    }
    const tick = () => setElapsedDisplay(formatElapsed(currentEntry.start_time));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [currentEntry]);

  const startTracking = async () => {
    if (!user) {
      toast({
        title: "Fehler",
        description: "Sie müssen angemeldet sein, um Zeit zu erfassen",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("time_entries")
        .insert({ start_time: new Date().toISOString(), user_id: user.id })
        .select()
        .single();

      if (error) throw error;

      setCurrentEntry(data);
      toast({ title: "Zeiterfassung gestartet", description: "Der Timer läuft" });
    } catch {
      toast({
        title: "Fehler",
        description: "Zeiterfassung konnte nicht gestartet werden",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const stopTracking = async () => {
    if (!currentEntry) return;

    setIsLoading(true);
    try {
      const endTime = new Date();
      const duration = calculateDurationSeconds(currentEntry.start_time, endTime.toISOString());

      const { error } = await supabase
        .from("time_entries")
        .update({ end_time: endTime.toISOString(), duration, description })
        .eq("id", currentEntry.id);

      if (error) throw error;

      setCurrentEntry(null);
      setDescription("");
      queryClient.invalidateQueries({ queryKey: ["time-entries"] });
      queryClient.invalidateQueries({ queryKey: ["available-months"] });
      toast({
        title: "Zeiterfassung gestoppt",
        description: `${Math.floor(duration / 60)} Minuten erfasst`,
      });
    } catch {
      toast({
        title: "Fehler",
        description: "Zeiterfassung konnte nicht gestoppt werden",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full sm:w-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex items-center space-x-3">
          <Clock className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium tabular-nums">{elapsedDisplay}</span>
          <Button
            variant={isTracking ? "destructive" : "default"}
            size="sm"
            disabled={isLoading}
            onClick={isTracking ? stopTracking : startTracking}
            className="w-full sm:w-auto transition-colors duration-150"
          >
            {isTracking ? (
              <>
                <Square className="mr-2 h-4 w-4" />
                Stop
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Start
              </>
            )}
          </Button>
        </div>
      </div>

      {isTracking && (
        <div className="mt-4 w-full animate-in fade-in-0 duration-200">
          <Label htmlFor="description">Woran arbeiten Sie?</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Beschreiben Sie Ihre aktuelle Tätigkeit..."
            className="mt-2"
          />
        </div>
      )}
    </div>
  );
}
